import { useSyncExternalStore } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toInstante, type Instante } from '@/lib/format';
import { cn } from '@/lib/utils';

const TICK_MS = 30_000;
const listeners = new Set<() => void>();
let ticker: number | undefined;

// One interval for the whole page: a table of 200 rows must not open 200 timers.
function subscribe(notify: () => void) {
  listeners.add(notify);
  ticker ??= window.setInterval(() => listeners.forEach((fn) => fn()), TICK_MS);
  return () => {
    listeners.delete(notify);
    if (listeners.size === 0 && ticker !== undefined) {
      window.clearInterval(ticker);
      ticker = undefined;
    }
  };
}

const now = () => Math.floor(Date.now() / TICK_MS);

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Lo que se dibuja cuando no hay fecha. Nunca una fecha relativa. */
export const SIN_FECHA = '—';

/**
 * `hace 3 h`, `en 2 d`, and an absolute date once the distance stops being useful.
 *
 * **Sin una fecha válida devuelve «—», jamás una distancia.** Es la mitad
 * visible de la regla que `toInstante` sostiene: una etiqueta relativa sólo se
 * produce a partir de un instante que existe. Antes esta función recibía un
 * `Date` y confiaba; `new Date(undefined)` daba `NaN` —que al menos se veía— y
 * `new Date(null)` daba el 1 de enero de 1970, que se dibujaba como «hace 56
 * años» sin que nadie sospechara nada.
 */
export function formatRelative(value: Instante, reference: Date = new Date()): string {
  const date = toInstante(value);
  if (date === null) return SIN_FECHA;

  const diff = reference.getTime() - date.getTime();
  const past = diff >= 0;
  const abs = Math.abs(diff);
  const wrap = (body: string) => (past ? `hace ${body}` : `en ${body}`);

  if (abs < MINUTE) return past ? 'recién' : 'ahora';
  if (abs < HOUR) return wrap(`${Math.round(abs / MINUTE)} min`);
  if (abs < DAY) return wrap(`${Math.round(abs / HOUR)} h`);
  if (abs < 7 * DAY) return wrap(`${Math.round(abs / DAY)} d`);
  if (date.getFullYear() === reference.getFullYear()) return format(date, "d 'de' MMM", { locale: es });
  return format(date, "d 'de' MMM yyyy", { locale: es });
}

/**
 * La fecha entera y exacta, o «—».
 *
 * Pasa por la misma puerta que su gemela relativa, y acá además evita una
 * caída: `format` de date-fns **lanza** con un `Invalid Date`, y media docena
 * de llamadas la invocaban con `new Date(algo?)` sin mirar. Una fecha que falta
 * no puede tumbar la pantalla que la muestra.
 */
export const formatAbsolute = (value: Instante) => {
  const date = toInstante(value);
  return date === null ? SIN_FECHA : format(date, "d 'de' MMMM 'de' yyyy, HH:mm", { locale: es });
};

export interface RelativeTimeProps {
  /** `null` is a real answer — "nobody knows" — and renders as a dash. */
  value: Instante;
  /** Show the absolute date as the body and the relative one on hover instead. */
  absolute?: boolean;
  className?: string;
}

/** Live-updating `<time>`: relative in the body, exact date in the tooltip. */
export function RelativeTime({ value, absolute = false, className }: RelativeTimeProps) {
  useSyncExternalStore(subscribe, now, now);

  // Una sola puerta para las tres formas en que una fecha puede no existir:
  // ausente, ilegible, o una marca en segundos que leída como milisegundos
  // aterriza en 1970. Ver `toInstante`.
  const date = toInstante(value);
  if (date === null) {
    return <span className={cn('text-muted-foreground', className)}>{SIN_FECHA}</span>;
  }

  const relative = formatRelative(date);
  const exact = formatAbsolute(date);

  return (
    <time dateTime={date.toISOString()} title={absolute ? relative : exact} className={className}>
      {absolute ? exact : relative}
    </time>
  );
}
